import React, { useState, useEffect, useMemo, useCallback } from "react";
import TemplateGrid from "./components/TemplateGrid/TemplateGrid";
import TemplateHeader from "./components/TemplateHeader/TemplateHeader";
import {
  createTheme,
  ThemeProvider,
  makeStyles,
} from "@material-ui/core/styles";
import { colors } from "../../components/theme";
import CompletedView from "./components/CompletedView/CompletedView";
import ArchiveView from "./components/ArchiveView/ArchiveView";
import PendingView from "./components/PendingView/PendingView";
import AssignedView from "./components/AssignedView/AssignedView";
import { useDispatch, useSelector } from "react-redux";
import { getSearchForm } from "../../store/search/actions";
import { ToastContainer } from "react-toastify";
import "./explorer-style.scss";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/loading";
import { getDocFormsToAssign, getDocFormPendingReadyForReview, getArchivedFormsByTemplateId } from "../../services/templateV2Service";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "90vh",
    gap: 20,
  },
  explorerContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflowY: "auto",
    flex: 1,
    height: "100%",
  },
}));

const theme = createTheme({
  palette: {
    primary: { main: colors.blue },
    secondary: { main: "#009A17" },
    green: { main: "#009A17" },
  },
  typography: {
    fontFamily: ["Host Grotesk", "Helvetica", "sans-serif"].join(","),
  },
});

const TemplatesV2 = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { responseSearchForm, loading : LoadingTemplates } = useSelector((state) => ({
    responseSearchForm: state.Search.responseSearchForm,
    loading: state.Search.loadingSearchForm,
  }));

  // Always provide a new array reference
  const listDocuments = useMemo(
    () => [...(responseSearchForm?.data || [])],
    [responseSearchForm?.data]
  );
  const listDocumentsTotal =
    responseSearchForm?.header?.totalRecords || listDocuments.length;
  const pageDetails = responseSearchForm?.header || {};

  const [page, setPage] = useState(1); // 1-based for API
  const [pageSize, setPageSize] = useState(25);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [valueSearch, setValueSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [assignedCount, setAssignedCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [archiveCount, setArchiveCount] = useState(0);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("templateViewMode") || "grid"
  );

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");

  // Sorting state
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleTabChange = (newValue) => setTabValue(newValue);

  const handleViewModeChange = (newViewMode) => {
    setViewMode(newViewMode);
    localStorage.setItem("templateViewMode", newViewMode);
  };

  const handlePageChange = (newPage, newPageSize) => {
    setPage(newPage + 1); // convert 0-based -> 1-based
    if (newPageSize) setPageSize(newPageSize);
  };

  const handleSort = (field) => {
    setPage(1); // reset to first page on sort
    setSortBy(field);
    setSortOrder((prev) =>
      sortBy === field ? (prev === "desc" ? "asc" : "desc") : "desc"
    );
  };

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setPage(1);
    if (nextSortBy) setSortBy(nextSortBy);
    if (nextSortOrder) setSortOrder(String(nextSortOrder).toLowerCase());
  };

  const handleSearchChange = (value) => {
    setValueSearch(value);
  };

  const handleSearch = (value) => {
    setValueSearch(value);
    setPage(1);
    // fetching is handled by the effect watching valueSearch/page/etc
  };

  // Debounce search input so we fetch as-you-type without spamming the API
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(valueSearch);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [valueSearch]);

  // Centralized fetch reacting to sort/pagination changes
  useEffect(() => {
    const obj = { text: debouncedSearch ? debouncedSearch.toLowerCase() : "" };
    dispatch(
      getSearchForm({ page, pageSize, data: obj, sortBy, sortOrder }, navigate)
    );
  }, [dispatch, navigate, page, pageSize, sortBy, sortOrder, debouncedSearch, refreshNonce]);

  // Fetch assigned count on component mount
  useEffect(() => {
    const fetchAssignedCount = async () => {
      try {
        const res = await getDocFormsToAssign();
        setAssignedCount(res.data.length);
      } catch (error) {
        console.error("Error fetching assigned count:", error);
        // Don't show error to user as this is just for the red dot indicator
      }
    };

    fetchAssignedCount();
  }, []);

  // Fetch sent (open) form request count on component mount
  useEffect(() => {
    const fetchSentCount = async () => {
      try {
        const res = await getDocFormPendingReadyForReview();
        const allSent = res.data || [];
        const stillOpen = allSent.filter(
          (item) => (item.status || "").toLowerCase() !== "completed"
        );
        setSentCount(stillOpen.length);
      } catch (error) {
        console.error("Error fetching sent form count:", error);
        // Don't show error to user as this is just for the red dot indicator
      }
    };

    fetchSentCount();
  }, []);

  const fetchArchiveCount = useCallback(async () => {
    try {
      const body = { text: "", isDeleted: true };
      const res = await getArchivedFormsByTemplateId(body, 1, 1);
      const items = res?.data?.data || [];
      const total = res?.data?.header?.totalRecords ?? items.length ?? 0;
      setArchiveCount(total);
    } catch (error) {
      console.error("Error fetching archive count:", error);
    }
  }, []);

  useEffect(() => {
    fetchArchiveCount();
  }, [fetchArchiveCount]);

  const setLoadingPage = (isLoading, message = "Processing...") => {
    setLoading(isLoading);
    setLoadingMessage(message);
  };

  const handleArchiveChanged = () => {
    setRefreshNonce((n) => n + 1);
    fetchArchiveCount();
  };

  return (
    <div>
      <ThemeProvider theme={theme}>
        <div className={classes.container}>
          <TemplateHeader
            tabValue={tabValue}
            onTabChange={handleTabChange}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            assignedCount={assignedCount}
            sentCount={sentCount}
            myTemplatesCount={listDocumentsTotal}
            completedCount={listDocumentsTotal}
            archiveCount={archiveCount}
            valueSearch={valueSearch}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />

          <div className={classes.explorerContainer}>
            {tabValue === 0 && (
              <TemplateGrid
                setLoading={setLoadingPage}
                reloadTemplates={() => {
                  // Force a refresh even if page is already 1
                  setRefreshNonce((n) => n + 1);
                }}
                onArchiveChanged={handleArchiveChanged}
                viewMode={viewMode}
                templates={listDocuments}
                loading={LoadingTemplates}
                pageDetails={pageDetails}
                currentPage={page - 1}
                pageSize={pageSize}
                totalRecords={listDocumentsTotal}
                onPageChange={handlePageChange}
                onSort={handleSort}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            )}
            {tabValue === 3 && (
              <CompletedView
                setLoading={setLoadingPage}
                viewMode={viewMode}
                onSort={handleSort}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            )}
            {tabValue === 1 && (
              <AssignedView
                setLoading={setLoadingPage}
                onAssignedCountChange={setAssignedCount}
              />
            )}
            {tabValue === 4 && (
              <ArchiveView
                setLoading={setLoadingPage}
                viewMode={viewMode}
                onArchiveChanged={handleArchiveChanged}
              />
            )}
            {tabValue === 2 && <PendingView setLoading={setLoadingPage} />}
          </div>

          <Loading show={loading} text={loadingMessage} />
          <ToastContainer autoClose={3000} />
        </div>
      </ThemeProvider>
    </div>
  );
};

export default TemplatesV2;
